import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";

import { getSummaryFile } from "../api/serverApi";

const SummaryModal = ({
  open,
  onClose,
  summaryResponse,
  documentId,
  accessToken,

  // =====================================================
  // EXISTING ACCEPTED SUMMARY
  // =====================================================
  summaryAccepted = false,

  // =====================================================
  // ACTIONS
  // =====================================================
  onResummarize,
  onAccept,
  onReject,
}) => {
  // =====================================================
  // NEW SUMMARY PREVIEW
  // =====================================================

  const [previewOpen, setPreviewOpen] = useState(false);

  // =====================================================
  // EXISTING SUMMARY PREVIEW
  // =====================================================

  const [existingPreviewOpen, setExistingPreviewOpen] =
    useState(false);

  // =====================================================
  // EXISTING SUMMARY TEXT
  // =====================================================

  const [existingSummaryText, setExistingSummaryText] =
    useState("");

  // =====================================================
  // EXISTING SUMMARY LOADING
  // =====================================================

  const [existingPreviewLoading, setExistingPreviewLoading] =
    useState(false);

  // =====================================================
  // EXISTING SUMMARY ERROR
  // =====================================================

  const [existingPreviewError, setExistingPreviewError] =
    useState("");

  // =====================================================
  // LOADING STATES
  // =====================================================

  const [acceptLoading, setAcceptLoading] =
    useState(false);

  const [rejectLoading, setRejectLoading] =
    useState(false);

  const [resummarizeLoading, setResummarizeLoading] =
    useState(false);

  // =====================================================
  // NEW SUMMARY TEXT
  // =====================================================

  const [previewText, setPreviewText] =
    useState("");

  // =====================================================
  // OPEN PREVIEW
  //
  // If summaryAccepted = true:
  //     Fetch existing .txt file
  //
  // Otherwise:
  //     Show newly generated summary
  // =====================================================

  const handlePreview = async () => {
    console.log(
      "========== SUMMARY PREVIEW =========="
    );

    console.log("Document ID:", documentId);
    console.log("Summary Accepted:", summaryAccepted);
    console.log("Summary Response:", summaryResponse);

    // =====================================================
    // EXISTING ACCEPTED SUMMARY
    // =====================================================

    if (summaryAccepted) {
      try {
        if (!documentId) {
          console.error(
            "Document ID is missing."
          );
          return;
        }

        if (!accessToken) {
          console.error(
            "Access token is missing."
          );
          return;
        }

        // Clear previous data
        setExistingSummaryText("");
        setExistingPreviewError("");

        // Open existing preview
        setExistingPreviewOpen(true);

        // Start loading
        setExistingPreviewLoading(true);

        console.log(
          "Fetching existing summary .txt file..."
        );

        const response = await getSummaryFile(
          documentId,
          accessToken
        );

        console.log(
          "========== EXISTING SUMMARY FILE RESPONSE =========="
        );

        console.log("FULL RESPONSE:", response);

        console.log(
          "CONTENT TYPE:",
          response?.headers?.["content-type"]
        );

        // =================================================
        // BACKEND RETURNS text/plain
        // =================================================

        let text = "";

        if (response?.data instanceof Blob) {
          text = await response.data.text();
        } else if (
          typeof response?.data === "string"
        ) {
          text = response.data;
        } else {
          text = String(
            response?.data || ""
          );
        }

        console.log(
          "EXISTING SUMMARY TEXT:",
          text
        );

        if (!text || !text.trim()) {
          throw new Error(
            "Summary file is empty."
          );
        }

        setExistingSummaryText(text);
      } catch (error) {
        console.error(
          "Failed to get existing summary:",
          error
        );

        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load existing summary.";

        setExistingPreviewError(message);
      } finally {
        setExistingPreviewLoading(false);
      }

      return;
    }

    // =====================================================
    // NEWLY GENERATED SUMMARY
    // =====================================================

    console.log(
      "Opening newly generated summary..."
    );

    // If summary is a string
    if (
      typeof summaryResponse?.summary ===
      "string"
    ) {
      setPreviewText(
        summaryResponse.summary
      );
    } else if (
      typeof summaryResponse === "string"
    ) {
      setPreviewText(
        summaryResponse
      );
    } else if (
      summaryResponse?.summary
    ) {
      // Object summary
      setPreviewText(
        JSON.stringify(
          summaryResponse.summary,
          null,
          2
        )
      );
    } else {
      setPreviewText("");
    }

    setPreviewOpen(true);
  };

  // =====================================================
  // CANCEL NEW SUMMARY PREVIEW
  //
  // Same behaviour as TranslationModal:
  // close preview + close parent modal
  // =====================================================

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewText("");

    onClose();
  };

  // =====================================================
  // CANCEL EXISTING SUMMARY PREVIEW
  //
  // IMPORTANT:
  //
  // This ONLY closes the .txt preview.
  //
  // It DOES NOT close:
  //
  // "Summary Already Exists"
  //
  // So user comes back to:
  //
  // CANCEL | PREVIEW | SUMMARY
  // =====================================================

  const handleExistingPreviewClose = () => {
    setExistingPreviewOpen(false);
    setExistingSummaryText("");
    setExistingPreviewError("");
  };

  // =====================================================
  // SUMMARIZE AGAIN
  // =====================================================

  const handleResummarize = async () => {
    try {
      setResummarizeLoading(true);

      console.log(
        "Starting re-summary..."
      );

      console.log(
        "Document ID:",
        documentId
      );

      // Start new summary
      await onResummarize(documentId);

      // Close existing summary popup
      onClose();
    } catch (error) {
      console.error(
        "Failed to start re-summary:",
        error
      );
    } finally {
      setResummarizeLoading(false);
    }
  };

  // =====================================================
  // DON'T SAVE / REJECT NEW SUMMARY
  // =====================================================

  const handleReject = async () => {
    try {
      setRejectLoading(true);

      // Close preview
      setPreviewOpen(false);

      // Close Summary Ready modal
      onClose();

      await onReject();
    } catch (error) {
      console.error(
        "Failed to reject summary:",
        error
      );
    } finally {
      setRejectLoading(false);
    }
  };

  // =====================================================
  // SAVE / ACCEPT NEW SUMMARY
  // =====================================================

  const handleAccept = async () => {
    try {
      setAcceptLoading(true);

      // Close preview
      setPreviewOpen(false);

      // Close Summary Ready modal
      onClose();

      await onAccept();
    } catch (error) {
      console.error(
        "Failed to accept summary:",
        error
      );
    } finally {
      setAcceptLoading(false);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      {/* =====================================================
          =====================================================
          EXISTING SUMMARY CONFIRMATION
          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          summaryAccepted &&
          !existingPreviewOpen
        }
        onClose={
          resummarizeLoading
            ? undefined
            : onClose
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Summary Already Exists
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              textAlign: "center",
              mt: 2,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              This document already has a
              summary.
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: "16px",
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              Please preview it before
              generating the summary again.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            pb: 3,
          }}
        >
          {/* =================================================
              CANCEL
          ================================================= */}

          <Button
            variant="outlined"
            onClick={onClose}
            disabled={resummarizeLoading}
          >
            CANCEL
          </Button>

          {/* =================================================
              PREVIEW EXISTING SUMMARY
          ================================================= */}

          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={
              existingPreviewLoading ||
              resummarizeLoading
            }
          >
            {existingPreviewLoading ? (
              <>
                <CircularProgress
                  size={18}
                  sx={{ mr: 1 }}
                />
                LOADING...
              </>
            ) : (
              "PREVIEW"
            )}
          </Button>

          {/* =================================================
              SUMMARY AGAIN
          ================================================= */}

          <Button
            variant="contained"
            onClick={handleResummarize}
            disabled={resummarizeLoading}
          >
            {resummarizeLoading ? (
              <>
                <CircularProgress
                  size={18}
                  sx={{
                    mr: 1,
                    color: "inherit",
                  }}
                />

                SUMMARIZING...
              </>
            ) : (
              "SUMMARY"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          =====================================================
          EXISTING SUMMARY TEXT PREVIEW
          =====================================================

          This is ONLY for viewing the already accepted
          summary.

          There is NO:
          
          SAVE
          DON'T SAVE

          Only:

          CANCEL

          Closing this returns to:

          Summary Already Exists

          CANCEL | PREVIEW | SUMMARY

          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          summaryAccepted &&
          existingPreviewOpen
        }
        onClose={
          existingPreviewLoading
            ? undefined
            : handleExistingPreviewClose
        }
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            Existing Summary Preview
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            backgroundColor: "#f5f5f5",
            p: 3,
          }}
        >
          {/* =================================================
              LOADING
          ================================================= */}

          {existingPreviewLoading && (
            <Box
              sx={{
                height: "100%",
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* =================================================
              ERROR
          ================================================= */}

          {!existingPreviewLoading &&
            existingPreviewError && (
              <Box
                sx={{
                  p: 3,
                  backgroundColor: "#fff",
                  borderRadius: 2,
                }}
              >
                <Typography
                  color="error"
                  sx={{
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {existingPreviewError}
                </Typography>
              </Box>
            )}

          {/* =================================================
              EXISTING SUMMARY TEXT
          ================================================= */}

          {!existingPreviewLoading &&
            !existingPreviewError &&
            existingSummaryText && (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  p: 3,
                  minHeight: "100%",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "inherit",
                    fontSize: "15px",
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {existingSummaryText}
                </Typography>
              </Box>
            )}

          {/* =================================================
              EMPTY
          ================================================= */}

          {!existingPreviewLoading &&
            !existingPreviewError &&
            !existingSummaryText && (
              <Box
                sx={{
                  height: "100%",
                  minHeight: "300px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography color="text.secondary">
                  Existing summary is not
                  available.
                </Typography>
              </Box>
            )}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={
              handleExistingPreviewClose
            }
            disabled={
              existingPreviewLoading
            }
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          =====================================================
          NEW SUMMARY READY
          =====================================================

          This is shown only when:

          summaryAccepted = false
          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          !summaryAccepted &&
          !previewOpen
        }
        onClose={
          acceptLoading ||
          rejectLoading
            ? undefined
            : onClose
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Summary Ready
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              textAlign: "center",
              mt: 3,
              mb: 2,
            }}
          >
            {/* =================================================
                SUCCESS ICON
            ================================================= */}

            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor:
                  "success.light",
                color: "success.main",
                fontSize: "32px",
                fontWeight: "bold",
              }}
            >
              ✓
            </Box>

            <Typography
              variant="h6"
              sx={{
                mt: 3,
                fontWeight: 600,
              }}
            >
              Summary Generated
              Successfully
            </Typography>

            <Typography
              sx={{
                mt: 2,
                color: "text.secondary",
              }}
            >
              The summary is ready
              for review.
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "text.secondary",
              }}
            >
              Please preview the summary
              before accepting it.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            pb: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePreview}
            disabled={!summaryResponse}
          >
            PREVIEW
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          =====================================================
          NEW SUMMARY TEXT PREVIEW
          =====================================================

          This preview has:

          CANCEL
          DON'T SAVE
          SAVE

          =====================================================
          ===================================================== */}

      <Dialog
        open={
          open &&
          !summaryAccepted &&
          previewOpen
        }
        onClose={
          acceptLoading ||
          rejectLoading
            ? undefined
            : handlePreviewClose
        }
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            Summary Preview
          </Typography>
        </DialogTitle>

        {/* =================================================
            TEXT
        ================================================= */}

        <DialogContent
          sx={{
            overflowY: "auto",
            backgroundColor: "#f5f5f5",
            p: 3,
          }}
        >
          {previewText ? (
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography
                component="pre"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "inherit",
                  fontSize: "15px",
                  lineHeight: 1.7,
                  margin: 0,
                }}
              >
                {previewText}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: "100%",
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary">
                Summary preview is not
                available.
              </Typography>
            </Box>
          )}
        </DialogContent>

        {/* =================================================
            ACTIONS
        ================================================= */}

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            py: 2,
          }}
        >
          {/* =================================================
              CANCEL
          ================================================= */}

          <Button
            variant="outlined"
            onClick={
              handlePreviewClose
            }
            disabled={
              acceptLoading ||
              rejectLoading
            }
          >
            CANCEL
          </Button>

          {/* =================================================
              DON'T SAVE
          ================================================= */}

          <Button
            variant="outlined"
            color="error"
            onClick={handleReject}
            disabled={
              acceptLoading ||
              rejectLoading
            }
          >
            {rejectLoading ? (
              <>
                <CircularProgress
                  size={18}
                  sx={{
                    mr: 1,
                  }}
                />

                UNSAVING...
              </>
            ) : (
              "DON'T SAVE"
            )}
          </Button>

          {/* =================================================
              SAVE
          ================================================= */}

          <Button
            variant="contained"
            color="success"
            onClick={handleAccept}
            disabled={
              acceptLoading ||
              rejectLoading
            }
          >
            {acceptLoading ? (
              <>
                <CircularProgress
                  size={18}
                  sx={{
                    mr: 1,
                    color: "inherit",
                  }}
                />

                SAVING...
              </>
            ) : (
              "SAVE"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SummaryModal;